import type { FieldError } from './errors'

/** The signed-in identity, in the app's terms. No Firebase type crosses this line. */
export type AuthUser = { uid: string; email: string | null; emailVerified: boolean }

/**
 * A second factor the account has enrolled with FIREBASE. Backend-sourced
 * factors are out of scope this phase — they need a `challenge_token` only
 * `POST /mfa/step-up/request` mints.
 */
export type MfaFactor = {
  id: string
  kind: 'totp' | 'phone'
  hint: string | null
  /**
   * Does the code exist yet, or must we ask for it to be created?
   *
   * `false` for an authenticator app — the code is already on the user's screen
   * and rotating. `true` for SMS, where nothing has been sent and a code box
   * would be asking the user to read a message that does not exist.
   *
   * A field rather than `kind === 'phone'` at the call site. The screen renders
   * whatever the port describes; deciding that phone means "send first" is the
   * adapter's knowledge, and the day a factor kind arrives that breaks the
   * correlation, one place is wrong instead of every place that inferred it.
   */
  needsDelivery: boolean
}

export type MfaChallenge = {
  /** Factors this client can actually present a code for. */
  factors: MfaFactor[]
  /**
   * Kinds the account HAS enrolled and this client cannot present.
   *
   * Required, not optional, and that is the point: an adapter that filters a
   * factor out has to say what it filtered. The first version dropped SMS
   * silently, and the screen — reasoning correctly that Firebase raises no
   * challenge for a backend-sourced factor — told those users their account
   * used Face ID and sent them to the iPhone app. The only cause it could not
   * be was the one it named. web-architect found it.
   *
   * De-duplicated: this answers "what kind", not "how many".
   */
  unpresentable: MfaFactor['kind'][]
  /**
   * Bring a factor's code into existence. Call before `submit` for any factor
   * whose `needsDelivery` is true; a no-op for the rest.
   *
   * **This is where the SMS is sent**, which is why it is a separate step and
   * not something `submit` could do. Firebase needs
   * `PhoneAuthProvider.verifyPhoneNumber(…, recaptchaVerifier)` to run before
   * the user has anything to type, and `submit(factorId, code)` has no moment
   * in which to do it — see #171. Without this step the screen could only put a
   * code box in front of a message nobody sent.
   *
   * **`container` is a real element the caller must render**, and passing it is
   * deliberate. `RecaptchaVerifier` mounts a widget; invisible reCAPTCHA still
   * mounts, can still challenge, and can still fail. An adapter that appended
   * its own div to `document.body` would put UI on screen that no component
   * owns and no test can see, and its failures would surface as an error with
   * nothing to point at. A DOM element is not a Firebase type — the rule this
   * port enforces is that the SDK stays behind it, not that the platform does.
   *
   * Safe to call again for the same factor: that is the "send me a new code"
   * path, and each call replaces the verification it will later accept.
   */
  prepare(factorId: string, container: HTMLElement): Promise<void>
  /**
   * Has a code already been brought into existence for this factor?
   *
   * The screen needs this on MOUNT, and it is the adapter that knows. A screen
   * keeping its own "I sent one" flag is a second source of truth with a
   * shorter life than the challenge: the challenge lives in auth state, the
   * flag dies with the component, and remounting is one browser Back away now
   * that the bare routes bounce a challenged user to `/mfa`. The screen would
   * then offer to send a code the user is already holding, and spend a second
   * SMS against the same quota.
   *
   * Always false for a factor whose `needsDelivery` is false — nothing had to
   * be brought into existence, so nothing was.
   */
  prepared(factorId: string): boolean
  submit(factorId: string, code: string): Promise<void>
}

/**
 * Thrown by `signIn` and `signInWithGoogle` when the account has a second factor.
 *
 * An exception rather than a union return, so a caller cannot forget the case:
 * `const user = await port.signIn(…)` either yields a signed-in user or does
 * not return at all. A `{ status }` union would typecheck while being ignored.
 */
export class MfaRequiredError extends Error {
  // Declared and assigned rather than a `readonly` constructor parameter: this
  // project sets `erasableSyntaxOnly`, under which a parameter property is
  // TS1294. The plan's version does not compile here.
  readonly challenge: MfaChallenge

  constructor(challenge: MfaChallenge) {
    super('mfa-required')
    this.name = 'MfaRequiredError'
    this.challenge = challenge
  }
}

/** Thrown for everything else. Carries the mapped field so a form can render it. */
export class AuthFailure extends Error {
  readonly detail: FieldError

  constructor(detail: FieldError) {
    super(detail.message)
    this.name = 'AuthFailure'
    this.detail = detail
  }
}

export type SignInProvider = 'password' | 'google'

export type AccountIdentity = {
  providers: SignInProvider[]
  unrecognised: string[]
  phoneNumber: string | null
}

/**
 * A second factor the account HAS, as Settings needs to list it.
 *
 * Distinct from `MfaFactor`, which describes a factor being challenged during
 * sign-in. The two answer different questions — "what can I present a code for
 * right now" versus "what is on my account" — and only this one carries when it
 * was added, which is what makes a list of three "Authenticator app" rows
 * tellable apart.
 */
export type EnrolledFactor = {
  id: string
  kind: 'totp' | 'phone'
  /** What the user named it, or the masked number Firebase reports. */
  label: string | null
  /** ISO 8601, or null where Firebase reported none. */
  enrolledAt: string | null
}

/**
 * A TOTP enrolment part-way through, holding the secret until it is confirmed.
 *
 * The secret is generated before the user proves they stored it, and it is not
 * on the account until `complete` succeeds — so abandoning this object is the
 * correct way to cancel. Nothing has to be undone.
 */
export type TotpEnrolment = {
  /** Base32, for someone who cannot scan. */
  secret: string
  /** `otpauth://` URI — what the QR encodes, and the same string either way. */
  uri: string
  complete(code: string, label: string): Promise<void>
}

/** A phone enrolment part-way through: the SMS is already sent. */
export type PhoneEnrolment = {
  complete(code: string, label: string): Promise<void>
}

export type SocialMode = 'popup' | 'redirect'

/**
 * What a social sign-in actually did — and the only field is the one nothing
 * else in the app can work out.
 *
 * Google sign-up and Google sign-in are ONE Firebase operation, so a screen
 * cannot tell them apart and neither can the provider. Firebase can:
 * `getAdditionalUserInfo(credential).isNewUser` is true exactly when the
 * account was created by this operation. Only the adapter sees a
 * `UserCredential`, so this is the only place that answer can enter the app.
 *
 * Without it the signup funnel was wrong in both directions — a popup reported
 * `signup_completed` for returning users, and a redirect could report nothing
 * at all because the document that started it is gone. See #172.
 */
export type SocialSignIn = { isNewUser: boolean }

export type AuthPort = {
  signIn(email: string, password: string): Promise<void>
  signUp(email: string, password: string): Promise<void>
  /**
   * Resolves with the outcome on `popup`, and with `null` on `redirect` —
   * where the document is about to be replaced and nothing has happened yet.
   * The redirect's outcome arrives later, through `completeRedirect`.
   */
  signInWithGoogle(mode: SocialMode): Promise<SocialSignIn | null>
  /**
   * Resolves a pending `signInWithRedirect`. No-op when there is none.
   *
   * **Returns the outcome when a redirect was actually consumed, and `null`
   * when there was none** — the only signal in the app that a social sign-in
   * has just succeeded, and now also whether it created the account. `signIn` and
   * the popup arm of `signInWithGoogle` resolve in the document that started
   * them, so the form can measure those itself; the redirect arm cannot,
   * because the document that started it is gone. Without this boolean the
   * coarse-pointer success path — every phone, per M1-R23 — is unmeasurable,
   * and reporting it at the tap instead counts sign-ins that never happened.
   *
   * The adapter implements it as `getRedirectResult()`, mapped through
   * `getAdditionalUserInfo`. A `null` result stays `null` here.
   *
   * **It must be consume-once per document, and that is now load-bearing.**
   * `AuthProvider` emits `signin_completed` on a `true`, and its subscribe
   * effect runs twice under `<StrictMode>` in development — mount, unmount,
   * mount. Firebase's `getRedirectResult()` clears the pending credential on
   * the first call, so the second returns null and only one event is sent. An
   * adapter that memoised the result instead would double-count every social
   * sign-in in dev, silently and only there. Do not cache it.
   */
  completeRedirect(): Promise<SocialSignIn | null>
  /**
   * What is on the account right now. Synchronous, because Firebase holds it on
   * the user object — there is no request to make.
   *
   * Returns `[]` when nobody is signed in rather than throwing: Settings can
   * render during a sign-out, and an empty list is the truthful answer.
   */
  enrolledFactors(): EnrolledFactor[]
  /**
   * Who the account IS, as the identity provider knows it. Synchronous, from
   * the user object.
   *
   * A provider id this port does not model is dropped from `providers` and
   * named in `unrecognised`, never mapped to the nearest thing — the same
   * stance as `MfaChallenge.unpresentable`. `phoneNumber` is the ACCOUNT phone
   * (a phone sign-in), not an MFA factor.
   */
  identity(): AccountIdentity
  /**
   * Start adding an authenticator app. Nothing reaches the account until the
   * returned `complete` succeeds.
   */
  beginTotpEnrolment(): Promise<TotpEnrolment>
  /**
   * Start adding a phone. **Sends the SMS**, so it is the enrolment twin of
   * `MfaChallenge.prepare` — and takes a container for the same reason: the
   * reCAPTCHA widget belongs to a component that can show its failure.
   */
  beginPhoneEnrolment(phoneNumber: string, container: HTMLElement): Promise<PhoneEnrolment>
  /**
   * Take a factor off the account.
   *
   * **The adapter refuses to remove the LAST live factor unless this user
   * re-authenticated in this tab within the last 5 minutes** (spent by a
   * successful removal, ended by sign-out; `requires-recent-login` otherwise),
   * so no screen can skip the step-up. A client-side gate only:
   * Firebase's own rule is its recent-login window, and server enforcement is
   * the backend's (#189).
   */
  removeFactor(factorId: string): Promise<void>
  /**
   * How THIS user proves who they are again — `null` when the account has no
   * provider this app can re-authenticate with (or nobody is signed in).
   *
   * The adapter's knowledge: which sign-in providers the account carries is on
   * Firebase's user object, and a screen guessing from "has an email" would
   * offer a password box to a Google-only account.
   */
  reauthMethod(): 'password' | 'google' | null
  /**
   * A fresh re-authentication, whatever the session's age. `password` is for
   * the `'password'` method and is ignored for `'google'`, which opens the
   * provider's popup.
   *
   * **Throws `MfaRequiredError` for an account with a second factor** — which
   * is exactly the account that reaches this, since the point is removing one.
   * Firebase refuses to re-authenticate such a user on the first factor alone;
   * the challenge's `submit` is what completes THIS re-authentication, not a
   * sign-in. Throws `AuthFailure` for a wrong password or a dismissed popup.
   *
   * The password is never stored, logged or returned.
   */
  reauthenticate(password?: string): Promise<void>
  sendPasswordReset(email: string): Promise<void>
  /**
   * Is this `oobCode` a live password-reset link, and whose?
   *
   * The email is the return value rather than a side effect, because
   * `ResetPassword` shows it in the subtitle ("Signed in as…", the design's
   * wording) before the user has typed anything — Firebase already knows it
   * from the code, and asking the user to retype an address they reached by
   * clicking a link in their inbox would be asking for a fact the link itself
   * carries.
   *
   * Throws `AuthFailure` for a dead code — `auth/expired-action-code` (past its
   * 30 minutes) or `auth/invalid-action-code` (already used, or malformed).
   * Both are terminal: the recovery is a new link, not a retry of this call.
   */
  verifyResetCode(oobCode: string): Promise<string>
  /**
   * Sets the new password and completes the reset.
   *
   * **Firebase revokes every refresh token on the account when this
   * succeeds**, unconditionally — there is no parameter to opt out, so this
   * port offers none either. A screen that drew a "sign out my other devices"
   * toggle would be offering a choice Firebase does not honour.
   *
   * Same two terminal codes as `verifyResetCode` can still arrive here: the
   * link is time-limited and single-use, and either can lapse in the gap
   * between the code being verified and the form being submitted.
   */
  completeReset(oobCode: string, newPassword: string): Promise<void>
  sendVerificationEmail(): Promise<void>
  signOut(): Promise<void>
  getIdToken(): Promise<string | null>
  /** Fires on sign-in, sign-out AND token refresh. Returns an unsubscribe. */
  onUserChanged(listener: (user: AuthUser | null) => void): () => void
}
